import {
  FlexBox,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  TooltipProps,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import { ChannelMetricType } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { PropsWithChildren, PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useMemo } from 'react';

import styles from './channelDashboardMetric.module.scss';

export type ChannelDashboardMetricProps = {
  /** The title of the metric. */
  title: string;
  /** The metric type. */
  metricType: ChannelMetricType;
  /** Whether to show the skeleton UI, e.g. when loading. */
  isSkeleton?: boolean;
  /** The props for the tooltip when hovering the alert icon. */
  alertTooltip?: Pick<TooltipProps, 'content' | 'head'> | null;
} & PropsWithChildren &
  PropsWithClassName;

export const ChannelDashboardMetric = ({
  className,
  children,
  title,
  metricType,
  isSkeleton,
  alertTooltip,
}: ChannelDashboardMetricProps): JSX.Element => {
  const handleHelperClick = useCallback(() => {
    ampli.dashboardMetricHelperClicked({
      dashboard: 'channels',
      metric: metricType,
    });
  }, [metricType]);

  const alertTooltipElement = useMemo(
    () =>
      alertTooltip &&
      !isSkeleton && (
        <Tooltip
          {...alertTooltip}
          className={styles.tooltip}
          wrap="div"
          wrapProps={{
            className: styles.tooltipWrapper,
          }}
        >
          <Icon
            name="exclamation-circle-filled"
            color={typography.color.error}
          />
        </Tooltip>
      ),
    [alertTooltip, isSkeleton],
  );

  return (
    <div className={classNames(styles.metric, className)}>
      <div className={styles.title}>
        {isSkeleton ? (
          <Skeleton height="16px" width="100px" />
        ) : (
          <>
            <Text.Paragraph as="span" variant="4" subdued>
              {title}
            </Text.Paragraph>

            <FlexBox
              as="span"
              role="button"
              data-dashboard-metric-helper={`channel_${metricType}`}
              data-elevio-allow-propagation
              onClick={handleHelperClick}
            >
              <Icon
                name="info"
                color={typography.color.tertiary}
                style={{ cursor: 'pointer' }}
              />
            </FlexBox>

            {alertTooltipElement}
          </>
        )}
      </div>

      {children}
    </div>
  );
};
